import { escapeHtml } from "./clmSerializers";

// Vendored index.html shell modelled on input PAXERA_KOKPIT_2026 reference deck (D3):
// same .presentation/.page/#page-N/.active DOM, same library
// include + initialize(), all real page transitions through
// CLMFunctions.HTMLFunctions.handleHotspotClick("#page-N"), and a same-file
// fallback that performs the identical .active toggle when the library is
// unreachable (file:// self-test). Source-tree module on purpose: dist/ is
// gitignored by directory and a runtime fetch would break the offline build.
export const CLM_LIBRARY_URL = "https://infiniapp.idesse.com/js/clm.presentation.library.js";

// DIVERGENCE FROM PAXERA REFERENCE (input PAXERA_KOKPIT_2026 reference deck).
// Nav/config/library surface match exactly, so navigation should
// hold — but anything the library binds AFTER initialize() against the
// reference's own DOM shape is UNVERIFIED here. Four differences, check this
// list first if live-smoke shows misplaced hotspots or a library feature that
// silently no-ops:
// 1. `.page` is a GRANDCHILD of `.presentation` (nested one level deeper
//    inside our `.clm-stage` scaler) instead of a direct child.
// 2. We don't emit the reference's `.image-container` / `.hotspot[data-x]` /
//    `.controls` / `.page-counter` classes — our DOM uses `.clm-img` /
//    `.clm-hs` / no controls / no counter instead.
// 3. The `.clm-stage` wrapper carries a `transform: scale(...)` (see fit()
//    below) that the reference's page container never applies.
// 4. No `positionHotspots` call (:88,309-316 in the reference): it wires
//    CLMFunctions.HTMLFunctions.positionHotspots on every page <img>'s
//    onload, because its hotspots are positioned as a percentage of that
//    rendered <img> element. Our deck never calls it: hotspots here are
//    positioned as a percentage of the fixed 1920x1080 .clm-stage, which is
//    sized/scaled independent of any image's load/render timing, so there's
//    nothing for positionHotspots to recompute against.

// Coerces the stage dims that get concatenated raw into emitted <script>/<style>
// text (libraryUrl already goes through JSON.stringify below). Today the caller
// always passes CLM_STAGE (fixed 1920x1080), so this is dead weight until a
// course-derived value is threaded through — kept as a defensive default, not
// a live bug.
function stageNumber(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

// Pure so it's unit-testable outside the emitted <script> template string
// (runtime() below stringifies this same formula into the deck's fit()).
export function computeStageFit(viewportW, viewportH, stageW, stageH) {
  const scale = Math.min(viewportW / stageW, viewportH / stageH);
  return {
    scale,
    left: Math.round((viewportW - stageW * scale) / 2),
    top: Math.round((viewportH - stageH * scale) / 2),
  };
}

function css(w, h) {
  return [
    "*{margin:0;padding:0;box-sizing:border-box}",
    "html,body{width:100%;height:100%;background:#000;overflow:hidden;font-family:Arial,Helvetica,sans-serif}",
    ".presentation{position:relative;width:100vw;height:100vh;overflow:hidden}",
    ".clm-stage{position:absolute;left:0;top:0;width:" +
      w +
      "px;height:" +
      h +
      "px;transform-origin:0 0;overflow:hidden}",
    ".page{position:absolute;left:0;top:0;width:100%;height:100%;display:none;overflow:hidden}",
    ".page.active{display:block}",
    ".clm-img{overflow:hidden}",
    ".clm-img img{display:block;width:100%;height:100%;transform-origin:50% 50%}",
    ".clm-txt{overflow:hidden;word-wrap:break-word}",
    ".clm-shape svg,.clm-art{display:block;width:100%;height:100%;border:0}",
    ".clm-hs{cursor:pointer;background:transparent;z-index:10;-webkit-tap-highlight-color:transparent}",
    ".clm-hs--off{cursor:default;pointer-events:none}",
    ".clm-ov{position:absolute;left:0;top:0;width:100%;height:100%;z-index:60;background:rgba(6,12,20,.62);display:flex;align-items:center;justify-content:center}",
    ".clm-ov[hidden]{display:none}",
    ".clm-ov-stage{position:relative;width:80%;height:80%;border-radius:18px;overflow:hidden;box-shadow:0 20px 52px rgba(0,0,0,.3)}",
    ".clm-ov-x{position:absolute;right:12px;top:12px;width:48px;height:48px;border:0;border-radius:12px;background:#EEF2F7;color:#42536B;font-size:28px;line-height:1;cursor:pointer;z-index:70}",
  ].join("\n");
}

function runtime(libraryUrl, w, h) {
  return [
    "(function () {",
    '  "use strict";',
    "  var STAGE_W = " + w + ", STAGE_H = " + h + ";",
    // Embeds computeStageFit's own transpiled source so the exported deck's
    // fit() and the unit-tested helper can never drift apart.
    "  var computeStageFit = " + computeStageFit.toString() + ";",
    '  function stage() { return document.getElementById("clm-stage"); }',
    "  function fit() {",
    "    var s = stage(); if (!s) return;",
    "    var f = computeStageFit(window.innerWidth, window.innerHeight, STAGE_W, STAGE_H);",
    '    s.style.transform = "scale(" + f.scale + ")";',
    '    s.style.left = f.left + "px";',
    '    s.style.top = f.top + "px";',
    "  }",
    "  function clmHas() {",
    '    return !!(window.CLMFunctions && CLMFunctions.HTMLFunctions && typeof CLMFunctions.HTMLFunctions.handleHotspotClick === "function");',
    "  }",
    "  // Offline fallback (file://): the same .active toggle the platform performs, so the deck self-tests without the library.",
    "  function localGo(id) {",
    "    var target = document.getElementById(id);",
    '    if (!target || !target.classList.contains("page")) return false;',
    '    var pages = document.querySelectorAll(".page");',
    '    for (var i = 0; i < pages.length; i++) pages[i].classList.remove("active");',
    '    target.classList.add("active");',
    "    return true;",
    "  }",
    "  window.clmGo = function (selector) {",
    '    var id = selector.charAt(0) === "#" ? selector.slice(1) : selector;',
    "    if (!/^page-\\d+$/.test(id) || !document.getElementById(id)) return false;",
    '    if (clmHas()) { CLMFunctions.HTMLFunctions.handleHotspotClick("#" + id); return true; }',
    "    return localGo(id);",
    "  };",
    "  // KÜB-style overlays are reporting-silent: DOM only, never the library.",
    "  window.clmOverlay = function (id) { var el = document.getElementById(id); if (el) el.hidden = false; };",
    '  window.clmOverlayClose = function () { var ovs = document.querySelectorAll(".clm-ov"); for (var i = 0; i < ovs.length; i++) ovs[i].hidden = true; };',
    "  function loadCLMFunctions() {",
    '    var script = document.createElement("script");',
    "    script.src = " + JSON.stringify(libraryUrl) + ";",
    "    script.onload = function () {",
    '      if (window.CLMFunctions && CLMFunctions.HTMLFunctions && typeof CLMFunctions.HTMLFunctions.initialize === "function") {',
    "        CLMFunctions.HTMLFunctions.initialize();",
    "      }",
    "    };",
    "    document.head.appendChild(script);",
    "  }",
    '  window.addEventListener("resize", fit);',
    '  window.addEventListener("orientationchange", fit);',
    '  document.addEventListener("DOMContentLoaded", function () { fit(); loadCLMFunctions(); });',
    "})();",
  ].join("\n");
}

function pageDiv(page, index) {
  return (
    '<div class="page' +
    (index === 0 ? " active" : "") +
    '" id="' +
    escapeHtml(page.id) +
    '" style="background:' +
    escapeHtml(page.background || "#000000") +
    '">\n' +
    page.html +
    "\n</div>"
  );
}

function overlayDiv(overlay) {
  return (
    '<div class="clm-ov" id="' +
    escapeHtml(overlay.id) +
    '" hidden>' +
    '<div class="clm-ov-stage" style="background:' +
    escapeHtml(overlay.background || "#ffffff") +
    '">\n' +
    overlay.html +
    // No per-course/runtime UI-chrome language signal exists (globalConfig
    // has no such field — the "language" seen elsewhere in server_utils.es6
    // is the TTS narration language, a different concept). Defaults to
    // English until one is threaded through; ties to the deferred
    // __editorLanguage/Turkish-chrome gap (P4 plan, binding ruling 9).
    '\n<button type="button" class="clm-ov-x" aria-label="Close" onclick="clmOverlayClose()">&times;</button>' +
    "</div></div>"
  );
}

export function renderClmIndexHtml({ title, pages, overlays, stage, libraryUrl }) {
  const w = stageNumber(stage && stage.width, 1920);
  const h = stageNumber(stage && stage.height, 1080);
  return [
    "<!DOCTYPE html>",
    // See the language note in overlayDiv() above — no per-course signal to
    // derive this from yet, defaults to English.
    '<html lang="en">',
    "<head>",
    '<meta charset="UTF-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1.0">',
    "<title>" + escapeHtml(title) + "</title>",
    "<style>",
    css(w, h),
    "</style>",
    "</head>",
    "<body>",
    '<div class="presentation">',
    '<div class="clm-stage" id="clm-stage">',
    (pages || []).map(pageDiv).join("\n"),
    (overlays || []).map(overlayDiv).join("\n"),
    "</div>",
    "</div>",
    "<script>",
    runtime(libraryUrl || CLM_LIBRARY_URL, w, h),
    "</script>",
    "</body>",
    "</html>",
  ].join("\n");
}
